"use client";

import React from "react";
import { ArrowRight, ShieldCheck, SlidersHorizontal, FileText, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  badgeCategory: string;
  h1: string;
  lead: string;
  ctaQuote: string;
  ctaResources: string;
  specLabel1: string;
  specVal1: string;
  specLabel2: string;
  specVal2: string;
  specLabel3: string;
  specVal3: string;
  specLabel4: string;
  specVal4: string;
  imgAlt: string;
  cardImageBadge: string;
  cardFooterNote: string;
  customServiceLink: string;
}
const copyData: Record<Lang, HeroCopy> = {
  en: {
    badgeCategory: "Woven & Sintered Wire Mesh",
    h1: "Engineered Stainless Steel Wire Mesh & Material Specifications",
    lead: "Precision-woven and sintered wire mesh solutions manufactured in Anping, Hebei for chemical, polymer, pharmaceutical, and liquid-solid separation applications. Alloy grades in AISI 304, 316L, 310S, Duplex 2205, and nickel alloys engineered for severe corrosive and high-temperature environments across 1 to 635 mesh counts.",
    ctaQuote: "Request a Quote",
    ctaResources: "Material Verification Guide",
    specLabel1: "Standard Alloys",
    specVal1: "AISI 304, 316L, 310S, Duplex",
    specLabel2: "Aperture Range",
    specVal2: "1 µm – 25 mm Precision",
    specLabel3: "Compliance",
    specVal3: "ASTM E2016 & ISO 9044",
    specLabel4: "Weave Structures",
    specVal4: "Plain, Twill, Dutch & Sintered",
    imgAlt: "High magnification stainless steel 316L woven wire mesh sample",
    cardImageBadge: "AISI 316L / 304L Mesh",
    cardFooterNote: "ASTM E2016 / ISO 9044 standard precision",
    customServiceLink: "Custom OEM Services"
  },
  zh: {
    badgeCategory: "编织与烧结工业丝网",
    h1: "工程级不锈钢丝网合金牌号与材料规格",
    lead: "MutualFilter（河北安平）为化工、聚合物、制药及工业固液分离应用提供精密编织丝网与烧结金属过滤材料。涵盖 AISI 304、316L、310S、双相钢 2205 及耐蚀镍基合金，支持 1 至 635 目与 1 µm 至 25 mm 精度范围定制。",
    ctaQuote: "索取报价",
    ctaResources: "材质验证指南",
    specLabel1: "标准合金牌号",
    specVal1: "AISI 304, 316L, 310S, Duplex",
    specLabel2: "过滤孔径范围",
    specVal2: "1 µm – 25 mm 精度范围",
    specLabel3: "执行制造标准",
    specVal3: "ASTM E2016 & ISO 9044",
    specLabel4: "编织与加工结构",
    specVal4: "平纹、斜纹、荷兰编织与烧结网",
    imgAlt: "高倍显微下的 AISI 316L 精密不锈钢编织丝网结构",
    cardImageBadge: "316L / 304L 精密编织网",
    cardFooterNote: "符合 ASTM E2016 / ISO 9044 工业公差标准",
    customServiceLink: "定制深加工服务"
  },
  es: {
    badgeCategory: "Mallas de alambre tejidas y sinterizadas",
    h1: "Mallas de alambre de acero inoxidable y especificaciones de materiales",
    lead: "Soluciones de malla metálica tejida y sinterizada fabricadas en Anping, Hebei, para aplicaciones químicas, poliméricas, farmacéuticas y de separación sólido-líquido. Calidades en AISI 304, 316L, 310S, Duplex 2205 y aleaciones de níquel para entornos corrosivos y de alta temperatura de 1 a 635 mallas.",
    ctaQuote: "Solicitar cotización",
    ctaResources: "Guía de verificación de materiales",
    specLabel1: "Aleaciones estándar",
    specVal1: "AISI 304, 316L, 310S, Duplex",
    specLabel2: "Rango de abertura",
    specVal2: "Precisión de 1 µm a 25 mm",
    specLabel3: "Normas de fabricación",
    specVal3: "ASTM E2016 e ISO 9044",
    specLabel4: "Estructuras de tejido",
    specVal4: "Liso, sarga, Dutch y sinterizado",
    imgAlt: "Muestra de malla de alambre tejida de acero inoxidable 316L de alta magnificación",
    cardImageBadge: "Malla AISI 316L / 304L",
    cardFooterNote: "Precisión estándar ASTM E2016 / ISO 9044",
    customServiceLink: "Servicios OEM a medida"
  },
  de: {
    badgeCategory: "Gewebte & gesinterte Drahtgewebe",
    h1: "Edelstahldrahtgewebe & Werkstoffspezifikationen",
    lead: "Präzisionsgewobene und gesinterte Drahtgewebelösungen gefertigt in Anping, Hebei, für Chemie, Polymere, Pharma und Fest-Flüssig-Trennung. Werkstoffgüten in AISI 304, 316L, 310S, Duplex 2205 und Nickelbasislegierungen für korrosive und hochtemperierte Prozesse von 1 bis 635 Mesh.",
    ctaQuote: "Angebot anfordern",
    ctaResources: "Leitfaden zur Materialprüfung",
    specLabel1: "Standardlegierungen",
    specVal1: "AISI 304, 316L, 310S, Duplex",
    specLabel2: "Maschenweitenbereich",
    specVal2: "1 µm – 25 mm Präzision",
    specLabel3: "Fertigungsnormen",
    specVal3: "ASTM E2016 & ISO 9044",
    specLabel4: "Bindungsarten",
    specVal4: "Leinwand, Köper, Tresse & Sintergewebe",
    imgAlt: "Hochauflösende Aufnahme eines AISI 316L Edelstahldrahtgewebes",
    cardImageBadge: "AISI 316L / 304L Gewebe",
    cardFooterNote: "Präzision nach ASTM E2016 / ISO 9044",
    customServiceLink: "Kundenspezifische OEM-Services"
  },
  fr: {
    badgeCategory: "Toiles tissées et frittées",
    h1: "Toiles métalliques en acier inoxydable et spécifications de matériaux",
    lead: "Solutions de toiles tissées et frittées de précision fabriquées à Anping, Hebei, pour les procédés chimiques, polymères, pharmaceutiques et la séparation solide-liquide. Nuances en AISI 304, 316L, 310S, Duplex 2205 et alliages de nickel pour environnements sévères de 1 à 635 mailles.",
    ctaQuote: "Demander un devis",
    ctaResources: "Guide de vérification des matériaux",
    specLabel1: "Alliages standard",
    specVal1: "AISI 304, 316L, 310S, Duplex",
    specLabel2: "Seuil de coupure",
    specVal2: "Précision de 1 µm à 25 mm",
    specLabel3: "Normes de référence",
    specVal3: "ASTM E2016 et ISO 9044",
    specLabel4: "Structures de tissage",
    specVal4: "Uni, sergé, hollandais et fritté",
    imgAlt: "Échantillon de toile métallique tissée en inox 316L à fort grossissement",
    cardImageBadge: "Toile AISI 316L / 304L",
    cardFooterNote: "Précision standard ASTM E2016 / ISO 9044",
    customServiceLink: "Services OEM sur mesure"
  },
  pt: {
    badgeCategory: "Telas tecidas e sinterizadas",
    h1: "Telas de arame em aço inoxidável e especificações de materiais",
    lead: "Soluções em malha de arame tecida e sinterizada de precisão fabricadas em Anping, Hebei, para separação químico-industrial, polímeros, farmacêutica e sólido-líquido. Graus de liga em AISI 304, 316L, 310S, Duplex 2205 e ligas de níquel para ambientes severos de 1 a 635 mesh.",
    ctaQuote: "Solicitar cotação",
    ctaResources: "Guia de verificação de materiais",
    specLabel1: "Ligas padrão",
    specVal1: "AISI 304, 316L, 310S, Duplex",
    specLabel2: "Faixa de abertura",
    specVal2: "Precisão de 1 µm a 25 mm",
    specLabel3: "Normas de conformidade",
    specVal3: "ASTM E2016 e ISO 9044",
    specLabel4: "Estruturas de tecelagem",
    specVal4: "Lisa, sarja, holandesa e sinterizada",
    imgAlt: "Amostra sob alta magnificação de tela de arame tecida em aço inoxidável 316L",
    cardImageBadge: "Tela AISI 316L / 304L",
    cardFooterNote: "Precisão padrão ASTM E2016 / ISO 9044",
    customServiceLink: "Serviços OEM sob medida"
  }
};
export default function TechnicalHero() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  const keySpecs = [{
    label: copy.specLabel1,
    value: copy.specVal1
  }, {
    label: copy.specLabel2,
    value: copy.specVal2
  }, {
    label: copy.specLabel3,
    value: copy.specVal3
  }, {
    label: copy.specLabel4,
    value: copy.specVal4
  }];
  return <section id="technicalhero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Text Column (7 Cols on lg) */}
          <div className="lg:col-span-7 flex flex-col items-start">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="rounded-md px-3 py-1 font-mono mf-caption border-border bg-card text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.badgeCategory}</span>
              </Badge>
              <Badge variant="outline" className="rounded-md px-3 py-1 font-mono mf-caption border-border bg-muted text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>ISO 9044 &amp; ASTM E2016</span>
              </Badge>
            </div>

            <h1 className="font-default text-balance mf-h1 text-foreground leading-tight">
              {copy.h1}
            </h1>

            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead leading-relaxed">
              {copy.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="gap-2 font-semibold">
                <Link to="/RequestAQuotePage">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" asChild className="gap-2 font-medium">
                <Link to="/StainlessSteelMaterialVerificationPage">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{copy.ctaResources}</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Visual Technical Panel (5 Cols on lg) */}
          <div className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              {/* Product Visual Container with Image from Page */}
              <div className="mf-card-media relative mf-ratio-hero w-full">
                <img src="https://mutualfilter.com/wp-content/uploads/2026/10/Schematic-diagram-of-metal-crystal-structure1.webp" alt={copy.imgAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.cardImageBadge}</span>
                </div>
              </div>

              {/* Card Footer Row: note + OEM link */}
              <div className="p-4 bg-card flex items-center justify-between gap-2">
                <span className="mf-caption text-muted-foreground truncate">
                  {copy.cardFooterNote}
                </span>
                <Link to="/OEMCustomMeshEngineeringPage" className="mf-caption font-medium text-primary hover:underline flex items-center gap-1 shrink-0">
                  <Layers className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.customServiceLink}</span>
                  <ArrowRight className="size-3" aria-hidden="true" />
                </Link>
              </div>
            </Card>
          </div>
        </div>

        {/* Spec Strip below grid inside mf-container */}
        <div className="mf-spec-strip">
          {keySpecs.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}